import { Outlet, useLocation } from 'react-router-dom';
import { useRef } from 'react';
import { Header } from './Header';
import { AppSidebar } from './AppSidebar';
import { SidebarProvider, SidebarTrigger } from './ui/sidebar';
import { useTenants } from '@/hooks/useTenants';
import { MobileNav } from './MobileNav';
import { InstallPrompt } from './pwa/InstallPrompt';
import { OfflineBanner } from './pwa/OfflineBanner';
import { useIsMobile } from '@/hooks/use-mobile';
import { MobilePageTransition } from './mobile/MobilePageTransition';
import { SwipeBackProvider } from './mobile/SwipeBackProvider';
import { PullToRefresh } from './mobile/PullToRefresh';
import { cn } from '@/lib/utils';

const LayoutContent = () => {
  // Populate tenant cache globally so all pages can resolve tenant IDs to names
  useTenants();
  const isMobile = useIsMobile();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const onMobileSurface = isMobile && location.pathname.startsWith('/m/');

  return (
    <div className="min-h-[100svh] bg-brand-background flex w-full">
      {!onMobileSurface && (
        <Header className="fixed top-0 left-0 right-0 z-50 mx-0 px-2 sm:px-[11px]">
          <div className="flex items-center gap-2">
            {!isMobile && <SidebarTrigger />}
          </div>
        </Header>
      )}
      {isMobile && <SwipeBackProvider />}
      <div className="flex w-full">
        {!isMobile && <AppSidebar />}
        <main
          ref={mainRef}
          className={cn(
            "flex-1 h-[100svh] md:pt-16 px-0 overflow-x-hidden overflow-y-auto min-w-0 max-w-full transition-all duration-300 ease-out will-change-transform overscroll-y-contain",
            onMobileSurface && "mobile-surface",
          )}
          style={{
            paddingTop: isMobile
              ? onMobileSurface
                ? 'env(safe-area-inset-top)'
                : 'calc(env(safe-area-inset-top) + 3.5rem)'
              : undefined,
            paddingBottom: isMobile
              ? 'calc(env(safe-area-inset-bottom) + 3.5rem)'
              : undefined,
          }}
        >


          {isMobile ? (
            <MobilePageTransition>
              <Outlet />
            </MobilePageTransition>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
      {isMobile && <PullToRefresh scrollTarget={mainRef.current} />}
      <MobileNav />
      <OfflineBanner />
      <InstallPrompt />
    </div>
  );
};

const getSidebarDefault = () => {
  if (typeof document === 'undefined') return true;
  const match = document.cookie.match(/sidebar:state=(\w+)/);
  return match ? match[1] === 'true' : true;
};

const Layout = () => {
  return (
    <SidebarProvider defaultOpen={getSidebarDefault()}>
      <LayoutContent />
    </SidebarProvider>
  );
};
export default Layout;
